/**
 * Leverancier toevoegen (23 okt 2026): formulier → eerst controleren (dry-run: de server
 * toont wat er in Airtable komt en of de persoon al bestaat), dan pas toevoegen. Dat
 * controle-overzicht is het vangnet: aanmaken in Airtable is niet vanuit hier terug te
 * draaien. Identiek op develop en main.
 *
 * Heractiveren (27 okt): bestaat de persoon al (bv. een oud-collega in Archive), dan toont het
 * formulier dat record en kan het met deze gegevens worden heractiveerd. Vangnet: bevestig met
 * oud → nieuw, daarna een toast "Ongedaan maken" die de oude waarden terugzet.
 */
import React, { useRef, useState } from 'react';
import {
  Alert, Autocomplete, Box, Button, Chip, Dialog, DialogActions, DialogContent, DialogTitle,
  MenuItem, TextField, Typography,
} from '@mui/material';
import { BACKEND_URL } from '../../utils/apiConfig';
import { authFetch } from '../../utils/authHelpers';
import { leesJson } from '../../utils/apiFout';
import { bevestig } from '../../utils/bevestig';
import { biedOngedaanAan } from '../../utils/ongedaan';
import { FUNCTIE_OPTIES, LEEG_NIEUW, nieuwNaarBody, wijzigingRegels } from './overzichtLogica';

const API = `${BACKEND_URL}/Api/leveranciers/nieuw`;
const recordApi = (id, wat) => `${BACKEND_URL}/Api/leveranciers/${encodeURIComponent(id)}/${wat}`;

async function post(url, body, wat) {
  const r = await authFetch(url, {
    method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(body),
  });
  return leesJson(r, wat);
}

async function stuur(body, dryRun) {
  const r = await authFetch(`${API}${dryRun ? '?dry_run=true' : ''}`, {
    method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(body),
  });
  if (r.status === 409) {
    const d = await r.json().catch(() => ({}));
    return { status: 'bestaat', melding: d?.detail?.melding || 'Bestaat al in Airtable.', bestaand: d?.detail?.bestaand || null };
  }
  return leesJson(r, dryRun ? 'Controleren' : 'Toevoegen');
}

const VELD_LABEL = {
  Voornaam: 'Voornaam', Tussenvoegsel: 'Tussenvoegsel', Achternaam: 'Achternaam', Bedrijfsnaam: 'Bedrijf',
  Categorie: 'Soort', Functie: 'Functies', Specialisatie: 'Specialisatie', 'Portfolio URL': 'Portfolio',
  Email: 'E-mail', Telefoonnummer: 'Telefoon', 'Adres (Stad)': 'Plaats', Dagtarief: 'Dagtarief',
  Uurtarief: 'Uurtarief', 'Freelancer Niveau': 'Niveau', Opmerkingen: 'Opmerking', Status: 'Status',
};

const STATUS_TEKST = { Active: 'actief', Archive: 'archief' };

export default function LeverancierToevoegen({ open, onClose, onToegevoegd, specialisaties = [] }) {
  const [f, setF] = useState(LEEG_NIEUW);
  const [voorstel, setVoorstel] = useState(null);
  const [fout, setFout] = useState(null);
  const [bezig, setBezig] = useState(false);
  // Elke wijziging of sluiten maakt een nieuwe "ronde"; een antwoord uit een oude ronde telt niet.
  const ronde = useRef(0);

  const zet = (k) => (e) => { ronde.current += 1; setVoorstel(null); setF((x) => ({ ...x, [k]: e.target.value })); };
  const zetLijst = (k) => (_e, v) => { ronde.current += 1; setVoorstel(null); setF((x) => ({ ...x, [k]: v })); };
  const sluit = () => { ronde.current += 1; setF(LEEG_NIEUW); setVoorstel(null); setFout(null); setBezig(false); onClose(); };

  // Toevoegen stuurt precies wat gecontroleerd is (velden zijn tijdens het controleren dicht).
  const doe = async (dryRun) => {
    setBezig(true); setFout(null);
    const body = dryRun ? nieuwNaarBody(f) : voorstel.body;
    const mijn = ronde.current;
    try {
      const d = await stuur(body, dryRun);
      if (!dryRun && d.status !== 'bestaat') { onToegevoegd?.(d); sluit(); return; }
      if (ronde.current === mijn) setVoorstel({ ...d, body });
    } catch (e) {
      if (ronde.current === mijn) setFout(e.message);
    } finally {
      if (ronde.current === mijn) setBezig(false);
    }
  };

  // Bestaand record bijwerken: eerst controleren (oud → nieuw), dan bevestigen, dan pas schrijven.
  const heractiveer = async () => {
    const { bestaand, body } = voorstel;
    const mijn = ronde.current;
    setBezig(true); setFout(null);
    try {
      const plan = await post(`${recordApi(bestaand.id, 'heractiveren')}?dry_run=true`, body, 'Controleren');
      if (ronde.current !== mijn) return;
      if (!(plan.wijzigingen || []).length) { setFout('Het bestaande record heeft deze gegevens al.'); return; }
      const ok = await bevestig({
        titel: `${plan.naam || bestaand.naam} heractiveren?`,
        tekst: wijzigingRegels(plan.wijzigingen, VELD_LABEL).join('\n'),
        knop: 'Heractiveren',
      });
      if (!ok || ronde.current !== mijn) return;
      // Wat bevestigd is gaat mee: wijkt het record intussen af, dan schrijft de server niets (409).
      const d = await post(recordApi(bestaand.id, 'heractiveren'),
        { ...body, verwacht: { oud: plan.oud, nieuw: plan.nieuw } }, 'Heractiveren');
      if (d.status === 'heractiveerd' && d.herstel) {
        const { herstel } = d;
        biedOngedaanAan(`${d.naam} is weer actief`, async () => {
          const t = await post(recordApi(bestaand.id, 'terugzetten'), { herstel }, 'Terugzetten');
          if ((t.overgeslagen || []).length) {
            throw new Error(`deels: ${t.overgeslagen.join(', ')} is intussen door iemand anders aangepast`);
          }
        }, { eigenKnop: true, duur: 15000 });
      }
      // Undo hoort bij de voltooide actie; sluiten alleen als het formulier niet intussen opnieuw is gebruikt.
      if (ronde.current !== mijn) return;
      onToegevoegd?.({ ...d, heractiveerd: true });
      sluit();
    } catch (e) {
      if (ronde.current === mijn) setFout(e.message);
    } finally {
      if (ronde.current === mijn) setBezig(false);
    }
  };

  const veld = { size: 'small', fullWidth: true, disabled: bezig };
  return (
    <Dialog open={open} onClose={sluit} maxWidth="sm" fullWidth>
      <DialogTitle sx={{ fontWeight: 800 }}>Leverancier toevoegen</DialogTitle>
      <DialogContent>
        <Box component="fieldset" disabled={bezig}
          sx={{ border: 0, m: 0, p: 0, minWidth: 0, display: 'grid', gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' }, gap: 1.5, pt: 1 }}>
          <TextField {...veld} label="Naam" required value={f.naam} onChange={zet('naam')} placeholder="Voornaam (tussenvoegsel) achternaam" />
          <TextField {...veld} label="Bedrijf" value={f.bedrijf} onChange={zet('bedrijf')} />
          <TextField {...veld} select label="Soort" value={f.categorie} onChange={zet('categorie')}>
            <MenuItem value="Freelancer">Freelancer (ZZP)</MenuItem>
            <MenuItem value="Bedrijf">Bedrijf</MenuItem>
          </TextField>
          <TextField {...veld} select label="Niveau" value={f.niveau} onChange={zet('niveau')}>
            <MenuItem value="">Onbekend</MenuItem>
            {['Junior', 'Medior', 'Senior'].map((n) => <MenuItem key={n} value={n}>{n}</MenuItem>)}
          </TextField>
          <Box sx={{ gridColumn: '1 / -1' }}>
            <Autocomplete disabled={bezig} multiple size="small" options={FUNCTIE_OPTIES} value={f.functies} onChange={zetLijst('functies')}
              renderTags={(v, p) => v.map((o, i) => <Chip size="small" label={o} {...p({ index: i })} key={o} />)}
              renderInput={(p) => <TextField {...p} label="Functies" />} />
          </Box>
          <Box sx={{ gridColumn: '1 / -1' }}>
            <Autocomplete disabled={bezig} multiple freeSolo size="small" options={specialisaties} value={f.specialisaties} onChange={zetLijst('specialisaties')}
              renderTags={(v, p) => v.map((o, i) => <Chip size="small" label={o} {...p({ index: i })} key={o} />)}
              renderInput={(p) => <TextField {...p} label="Specialisatie" helperText="Een nieuwe specialisatie (bv. Camjo) wordt in Airtable aangemaakt" />} />
          </Box>
          <TextField {...veld} label="Portfolio" value={f.portfolio} onChange={zet('portfolio')} placeholder="https://" />
          <TextField {...veld} label="E-mail" value={f.email} onChange={zet('email')} />
          <TextField {...veld} label="Telefoon" value={f.telefoon} onChange={zet('telefoon')} />
          <TextField {...veld} label="Plaats" value={f.plaats} onChange={zet('plaats')} />
          <TextField {...veld} label="Dagtarief (€)" value={f.dagtarief} onChange={zet('dagtarief')} inputMode="decimal" />
          <TextField {...veld} label="Uurtarief (€)" value={f.uurtarief} onChange={zet('uurtarief')} inputMode="decimal" />
          <TextField {...veld} label="Opmerking" value={f.opmerking} onChange={zet('opmerking')} multiline minRows={2} sx={{ gridColumn: '1 / -1' }} />
        </Box>
        {fout && <Alert severity="error" sx={{ mt: 2 }}>{fout}</Alert>}
        {voorstel?.status === 'bestaat' && (
          <Alert severity="warning" sx={{ mt: 2 }}>
            {voorstel.melding}
            {voorstel.bestaand?.id && (
              <Box sx={{ mt: 1 }}>
                <Typography sx={{ fontSize: 13 }}>
                  {[voorstel.bestaand.naam, voorstel.bestaand.categorie,
                    STATUS_TEKST[voorstel.bestaand.status] || voorstel.bestaand.status, voorstel.bestaand.email]
                    .filter(Boolean).join(' · ')}
                </Typography>
                <Button size="small" variant="outlined" color="inherit" disabled={bezig} onClick={heractiveer}
                  sx={{ textTransform: 'none', mt: 1 }}>
                  Bestaand record heractiveren met deze gegevens
                </Button>
              </Box>
            )}
          </Alert>
        )}
        {voorstel?.status === 'dry_run' && (
          <Box sx={{ mt: 2, p: 1.5, bgcolor: '#f6f7f9', borderRadius: 1 }}>
            <Typography sx={{ fontSize: 13, fontWeight: 700, mb: 0.5 }}>Dit komt in Airtable</Typography>
            {Object.entries(voorstel.velden || {}).filter(([k]) => k !== 'Status').map(([k, v]) => (
              <Typography key={k} sx={{ fontSize: 12.5 }}>
                <span style={{ color: '#556071' }}>{VELD_LABEL[k] || k}: </span>{Array.isArray(v) ? v.join(', ') : String(v)}
              </Typography>
            ))}
            {(voorstel.nieuweSpecialisaties || []).length > 0 && (
              <Typography sx={{ fontSize: 12.5, color: '#8a5a00', mt: 0.5 }}>
                Nieuwe specialisatie in Airtable: {voorstel.nieuweSpecialisaties.join(', ')}
              </Typography>
            )}
          </Box>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={sluit} sx={{ textTransform: 'none' }}>Annuleren</Button>
        {voorstel?.status === 'dry_run' ? (
          <Button variant="contained" disabled={bezig} onClick={() => doe(false)} sx={{ textTransform: 'none' }}>
            {bezig ? 'Bezig…' : 'Toevoegen in Airtable'}
          </Button>
        ) : (
          <Button variant="outlined" disabled={bezig || !f.naam.trim()} onClick={() => doe(true)} sx={{ textTransform: 'none' }}>
            {bezig ? 'Bezig…' : 'Controleren'}
          </Button>
        )}
      </DialogActions>
    </Dialog>
  );
}
